/**
 * TileSelector.jsx
 *
 * Single-select grid of radio tiles. Shared UI primitive consumed by
 * the Tool Finder wizard steps:
 *   - D3a StepApplication
 *   - D3b StepDrillingMethod
 *   - D3c StepEquipmentClass
 *   - D3e StepRockConditions
 *
 * Behaves as a WAI-ARIA radiogroup with roving tabindex and arrow-key
 * navigation. Mirrors the visual conventions of StepEntryPoint.jsx
 * (border, hover lift, focus ring, typography).
 *
 * Brand rules respected:
 *   - No rounded corners (Sandvik brand — see tailwind.config.js).
 *   - Electric Blue selected state at sub-5% tint (bg-electric-blue/5).
 *   - Icon variant uses the eb-1 text-chip placeholder pattern; real
 *     artwork is a parked workstream.
 *
 * @module components/ui/TileSelector
 * @since 2026-05-17 (Group D Sub-Plan 2, Task D4)
 */

import { useCallback, useMemo, useRef } from 'react';

/**
 * @typedef {Object} Tile
 * @property {string} id
 * @property {string} label
 * @property {string} [sublabel]
 * @property {string} [icon]            text placeholder for variant='icon'
 * @property {string} [image]           image src for variant='image'
 * @property {boolean} [disabled]
 * @property {string} [disabledReason]
 */

/**
 * Tailwind purges unused classes, so we keep a small whitelist of
 * grid templates and resolve at runtime. Adding a new template?
 * Append a row here — never build the class via a template literal.
 *
 * Keys are `${mobile}-${tablet}-${desktop}`.
 */
const GRID_TEMPLATES = {
  '1-2-3': 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3',
  '2-3-4': 'grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4',
  '2-2-3': 'grid grid-cols-2 md:grid-cols-2 lg:grid-cols-3',
  '1-3-3': 'grid grid-cols-1 md:grid-cols-3 lg:grid-cols-3',
};

const DEFAULT_COLS = { mobile: 2, tablet: 3, desktop: 4 };

function gridClassFor(cols) {
  const m = cols?.mobile ?? DEFAULT_COLS.mobile;
  const t = cols?.tablet ?? DEFAULT_COLS.tablet;
  const d = cols?.desktop ?? DEFAULT_COLS.desktop;
  const key = `${m}-${t}-${d}`;
  return GRID_TEMPLATES[key] ?? GRID_TEMPLATES['2-3-4'];
}

/**
 * Find the next non-disabled tile index in a given direction.
 * Returns the original index if no non-disabled neighbour is reachable.
 *
 * @param {Tile[]} tiles
 * @param {number} from
 * @param {number} step  positive or negative integer
 * @returns {number}
 */
function nextEnabledIndex(tiles, from, step) {
  if (step === 0) return from;
  let i = from + step;
  while (i >= 0 && i < tiles.length) {
    if (!tiles[i].disabled) return i;
    i += step > 0 ? 1 : -1;
  }
  return from;
}

/**
 * First / last enabled tile index. Used for Home/End.
 *
 * @param {Tile[]} tiles
 * @param {'first'|'last'} edge
 * @returns {number}
 */
function edgeEnabledIndex(tiles, edge) {
  if (edge === 'first') {
    for (let i = 0; i < tiles.length; i += 1) {
      if (!tiles[i].disabled) return i;
    }
  } else {
    for (let i = tiles.length - 1; i >= 0; i -= 1) {
      if (!tiles[i].disabled) return i;
    }
  }
  return 0;
}

/**
 * TileSelector — single-select radio grid.
 *
 * @param {Object} props
 * @param {Tile[]} props.tiles
 * @param {string|null} props.value
 * @param {(id: string) => void} props.onChange
 * @param {{mobile?: number, tablet?: number, desktop?: number}} [props.cols]
 * @param {'icon'|'image'} [props.variant]
 * @param {string} [props.ariaLabel]
 */
export default function TileSelector({
  tiles,
  value,
  onChange,
  cols = DEFAULT_COLS,
  variant = 'icon',
  ariaLabel,
}) {
  const tileRefs = useRef([]);
  const desktopCols = cols?.desktop ?? DEFAULT_COLS.desktop;

  // Determine which tile owns the tab stop at rest (roving tabindex).
  // - If a tile is selected, it is the tab stop.
  // - Otherwise, the first non-disabled tile.
  const rovingIndex = useMemo(() => {
    const selectedIdx = tiles.findIndex((t) => t.id === value && !t.disabled);
    if (selectedIdx >= 0) return selectedIdx;
    return edgeEnabledIndex(tiles, 'first');
  }, [tiles, value]);

  const focusIndex = useCallback((idx) => {
    const node = tileRefs.current[idx];
    if (node && typeof node.focus === 'function') {
      node.focus();
    }
  }, []);

  const handleKeyDown = useCallback(
    (event, currentIdx) => {
      const { key } = event;

      // Selection keys — Enter and Space select the focused tile.
      // (Clicking the already-selected tile is a no-op; see handleClick.)
      if (key === 'Enter' || key === ' ' || key === 'Spacebar') {
        event.preventDefault();
        const tile = tiles[currentIdx];
        if (tile && !tile.disabled && tile.id !== value) {
          onChange(tile.id);
        }
        return;
      }

      let nextIdx = null;
      switch (key) {
        case 'ArrowRight':
          nextIdx = nextEnabledIndex(tiles, currentIdx, 1);
          break;
        case 'ArrowLeft':
          nextIdx = nextEnabledIndex(tiles, currentIdx, -1);
          break;
        case 'ArrowDown':
          nextIdx = nextEnabledIndex(tiles, currentIdx, desktopCols);
          break;
        case 'ArrowUp':
          nextIdx = nextEnabledIndex(tiles, currentIdx, -desktopCols);
          break;
        case 'Home':
          nextIdx = edgeEnabledIndex(tiles, 'first');
          break;
        case 'End':
          nextIdx = edgeEnabledIndex(tiles, 'last');
          break;
        default:
          return;
      }

      if (nextIdx !== null && nextIdx !== currentIdx) {
        event.preventDefault();
        focusIndex(nextIdx);
      } else if (nextIdx !== null) {
        // Even if we don't move, swallow the arrow so the page doesn't scroll.
        event.preventDefault();
      }
    },
    [tiles, value, onChange, desktopCols, focusIndex],
  );

  const handleClick = useCallback(
    (tile) => {
      if (tile.disabled) return;
      if (tile.id === value) return; // No-op: re-selecting the current tile.
      onChange(tile.id);
    },
    [value, onChange],
  );

  return (
    <div
      role="radiogroup"
      aria-label={ariaLabel}
      className={`${gridClassFor(cols)} gap-3`}
    >
      {tiles.map((tile, idx) => {
        const isSelected = tile.id === value && !tile.disabled;
        const isTabStop = idx === rovingIndex;

        const stateClass = isSelected
          ? 'border-2 border-electric-blue bg-electric-blue/5'
          : 'border border-n3 bg-white';

        const interactionClass = tile.disabled
          ? 'opacity-50 cursor-not-allowed'
          : 'hover:border-n5 hover:shadow-sm hover:-translate-y-0.5';

        return (
          <button
            key={tile.id}
            ref={(el) => {
              tileRefs.current[idx] = el;
            }}
            type="button"
            role="radio"
            aria-checked={isSelected}
            aria-disabled={tile.disabled || undefined}
            disabled={tile.disabled || undefined}
            tabIndex={isTabStop ? 0 : -1}
            onClick={() => handleClick(tile)}
            onKeyDown={(e) => handleKeyDown(e, idx)}
            className={[
              'group relative flex flex-col items-center text-center p-4 rounded-none',
              'transition-all focus:outline-none focus:ring-2 focus:ring-electric-blue',
              stateClass,
              interactionClass,
            ].join(' ')}
          >
            {variant === 'image' && tile.image ? (
              <img
                src={tile.image}
                alt=""
                className="h-16 mx-auto object-contain"
              />
            ) : (
              <span
                className="inline-flex items-center justify-center w-12 h-12 mb-3 bg-eb-1 text-electric-blue text-xs font-normal tracking-wide"
                aria-hidden="true"
              >
                {tile.icon ?? ''}
              </span>
            )}

            <span className="font-sandvik-headline text-base font-normal text-carbon-black">
              {tile.label}
            </span>

            {tile.sublabel && (
              <span className="font-sandvik-text text-xs text-n6 mt-0.5">
                {tile.sublabel}
              </span>
            )}

            {tile.disabled && tile.disabledReason && (
              <span
                className="font-sandvik-text text-xs text-n5 mt-1 italic"
                data-testid="tile-disabled-reason"
              >
                {tile.disabledReason}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}
